import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Icon, Button } from '@cbrebuild/blocks';

import LoadingBar from '../../nucleus/loading-bar/loading-bar';
import Tooltip from '../../nucleus/tooltip/tooltip';
import DealMembers from '../../components/headers/deal-detail-header/deal-members';
import AdminVoucherSubmissionTable from './admin-voucher-submission-table';
import AdminConnectorVoucherAccessNew from './admin-manage-deal-connector-voucher-access';
import VoucherAccessConditionalButton from './voucher-access-conditional-button';
import RowErrorBoundary from '../../components/error-boundary/row-error-boundary';
import { formatDealTypeAndRole, formatDealStage, prepareVoucherData, getUserName, getLeadProducerName, getDealDetailsPath, getDealDetailsUrl } from '../../utils/deal-display-utils';
import { getAvatarColor } from '../../utils/avatar-utils';
import * as adminService from '../../services/admin-service';
import copyIcon from '../../assets/images/svg/copy-icon.svg';

const AdminDealDetails = ({
  deal,
  expandedDealId,
  loadingTeamData,
  dealUsers,
  dealTeams,
  reduxDeal,
  addAllAdminDealMembers,
  getCommissionUserIds,
  loadingTransactionId,
  selectedTransactionId,
  handleLinkedMtaTransactionClick,
  formatDate,
  showVoucherSubmissions,
  handleVoucherSubmissionsClick,
  userData,
  toast,
  commissionOverviewSection // This will be the commission overview component
}) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const hasValidDealId = Boolean(deal?.id);
  const dealDetailsPath = useMemo(() => (hasValidDealId ? getDealDetailsPath(deal) : null), [deal, hasValidDealId]);
  const dealDetailsUrl = useMemo(() => (hasValidDealId ? getDealDetailsUrl(deal) : null), [deal, hasValidDealId]);

  const handleCopyDealLink = async (event) => {
    event.stopPropagation();

    if (!dealDetailsUrl) {
      if (toast) {
        toast('Unable to copy link. Deal ID is missing.', 'error');
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(dealDetailsUrl);
      setLinkCopied(true);
    } catch (error) {
      if (toast) {
        toast('Failed to copy link. Please try again.', 'error');
      }
    }
  };

  // Get all user IDs that are already part of teams to suppress individual display
  const getUserIdsFromTeams = () => {
    if (!Array.isArray(dealTeams)) return [];
    
    const teamUserIds = [];
    dealTeams.forEach(teamItem => {
      if (teamItem?.team?.users && Array.isArray(teamItem.team.users)) {
        teamItem.team.users.forEach(user => {
          if (user?.id) {
            teamUserIds.push(user.id);
          }
        });
      }
    });
    return teamUserIds;
  };

  // Get all users to suppress from add/remove modal (users who are already team members)
  const getAllUsersToSuppress = () => {
    const usersToSuppress = new Set();
    if (Array.isArray(dealUsers)) {
      dealUsers.forEach((tm) => { 
        if (tm?.user?.id) {
          usersToSuppress.add(tm.user.id); 
        }
      });
    }
    if (Array.isArray(dealTeams)) {
      dealTeams.forEach((tm) => {
        if (tm?.team?.users && Array.isArray(tm.team.users)) {
          tm.team.users.forEach((user) => {
            if (user?.id) {
              usersToSuppress.add(user.id);
            }
          });
        }
      });
    }
    return Array.from(usersToSuppress);
  };

  // For avatar display - only suppress team members to avoid duplication
  const adminUsersToSuppress = getUserIdsFromTeams();
  
  // For add/remove modal - suppress all existing members
  const adminModalUsersToSuppress = getAllUsersToSuppress();

  // State for connector voucher access
  const [showConnectorAccess, setShowConnectorAccess] = useState(false);
  
  // State for deal restrictions - fetch once and share with both components
  const [dealRestrictions, setDealRestrictions] = useState(null);
  const [restrictionsLoading, setRestrictionsLoading] = useState(false);

  // Fetch deal restrictions once when deal or team members change
  useEffect(() => {
    const fetchDealRestrictions = async () => {
      const userIds = [];
      
      if (Array.isArray(dealUsers)) {
        dealUsers.forEach(userItem => {
          if (userItem?.user?.id) {
            userIds.push(userItem.user.id);
          }
        });
      }
      
      if (Array.isArray(dealTeams)) {
        dealTeams.forEach(teamItem => {
          if (Array.isArray(teamItem?.team?.users)) {
            teamItem.team.users.forEach(user => {
              if (user?.id && !userIds.includes(user.id)) {
                userIds.push(user.id);
              }
            });
          }
        });
      }

      if (userIds.length === 0) {
        setDealRestrictions({});
        return;
      }

      try {
        setRestrictionsLoading(true);
        const restrictionsMap = await adminService.fetchUserDealRestrictionsByUserIds(userIds);
        setDealRestrictions(restrictionsMap);
      } catch (error) {
        console.error('Error fetching deal restrictions');
        setDealRestrictions({});
      } finally {
        setRestrictionsLoading(false);
      }
    };

    if (deal && expandedDealId === deal.deal_iq_id) {
      fetchDealRestrictions();
    }
  }, [deal?.id, dealUsers, dealTeams, expandedDealId, deal?.deal_iq_id]);

  // Use actual connector ID from deal (no fallback)
  const connectorId = deal.connector_id;

  // Handler functions for connector voucher access
  const handleConnectorAccessToggle = () => {
    setShowConnectorAccess(prev => !prev);
  };

  return (
    <RowErrorBoundary>
      <div className="expanded-details">
        <div className="deal-card-unified">
                {/* Header Section with Deal Name, Property Info and Status */}
                <div className="card-header">
                  <div className="header-layout admin-header-layout-relative">
                    <div className="deal-title-section">
                      <h3 className="deal-title">{deal.deal_name}</h3>


                      <div className="admin-flex-container">
                        {/* Property Information - Left side */}
                        <div className="property-details-section admin-property-details-section">
                          {deal?.properties?.[0] && (deal?.properties?.[0]?.name || deal?.properties?.[0]?.address) && (
                            <div className="property-info-block admin-property-info-block">
                              {deal?.properties?.[0]?.name && (
                                <div className="property-name admin-property-name">{deal.properties[0].name}</div>
                              )}
                              {deal?.properties?.[0]?.address && (
                                <div className="property-address admin-property-address">{deal.properties[0].address}</div>
                              )}
                              {(deal?.properties?.[0]?.city || deal?.properties?.[0]?.state_abbreviation || deal?.properties?.[0]?.postal_code) && (
                                <div className="property-location admin-property-location">
                                  {deal?.properties?.[0]?.city ? deal.properties[0].city.toUpperCase() : ''}
                                  {deal?.properties?.[0]?.city && (deal?.properties?.[0]?.state_abbreviation || deal?.properties?.[0]?.postal_code) ? ', ' : ''}
                                  {deal?.properties?.[0]?.state_abbreviation ? deal.properties[0].state_abbreviation.toUpperCase() : ''}
                                  {deal?.properties?.[0]?.state_abbreviation && deal?.properties?.[0]?.postal_code ? ' ' : ''}
                                  {deal?.properties?.[0]?.postal_code ? deal.properties[0].postal_code : ''}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {hasValidDealId && (
                        <div className="deal-link-row">
                          <Link
                            className="deal-link-url"
                            to={dealDetailsPath}
                            onClick={event => event.stopPropagation()}
                          >
                            {dealDetailsUrl}
                          </Link>
                          <Tooltip message="Copy Link" position="top" size="small">
                            <button
                              type="button"
                              aria-label="Copy Link"
                              className="copy-deal-link-button"
                              disabled={!dealDetailsUrl}
                              onClick={handleCopyDealLink}
                            >
                              {linkCopied
                                ? <Icon iconName="checkmark" size="large" />
                                : <img className="copy-deal-link-icon" src={copyIcon} alt="" />}
                            </button>
                          </Tooltip>
                          {linkCopied && <span className="copy-deal-link-status">Copied</span>}
                        </div>
                      )}
                    </div>
                    
                    {/* Team Members Section - Absolute positioned at right corner */}
                    <div className="team-members-header-section">
                        {loadingTeamData === deal.id ? (
                          <div className="team-loading">
                            <LoadingBar />
                            <span>Loading team members...</span>
                          </div>
                        ) : expandedDealId === deal.deal_iq_id ? (
                          <>
                            <DealMembers
                            dealUsers={Array.isArray(dealUsers) ? dealUsers.filter(user => user && user.user && user.user.id) : []}
                            dealTeams={Array.isArray(dealTeams) ? dealTeams.filter(teamItem => teamItem && teamItem.team && teamItem.team.id).map(teamItem => ({
                              id: teamItem.team.id,
                              name: teamItem.team.name,
                              avatar_color: teamItem.team.avatar_color,
                              users: Array.isArray(teamItem.team.users) ? teamItem.team.users : []
                            })) : []}
                            showAddOrRemove={true}
                            maxAvatarsToShow={10}
                            usersToSuppress={adminUsersToSuppress}
                            teamsToSuppress={[]}
                            commissionedUserIds={getCommissionUserIds(deal) || []}
                            analyticProperties={{
                              categoryPrefix: 'Admin Manage Deal',
                              actionPrefix: 'admin_team_members'
                            }}
                            addDealMembers={(userIds) => {
                              if (!reduxDeal || !reduxDeal.id) {
                                console.warn('addDealMembers: reduxDeal or reduxDeal.id is undefined');
                                return;
                              }
                              addAllAdminDealMembers(reduxDeal.id, userIds);
                            }}
                          />
                          </>
                        ) : (
                          <div className="no-collaborators">No team members found for this deal</div>
                        )}
                      </div>
                  </div>
                </div>

                {/* Deal Details Grid */}
                <div className="card-content admin-reduced-spacing">
                  <div className="deal-info-grid">
                    {/* Render detail section in new order */}
                    <div className="info-item">
                      <div className="info-label">DEAL IQ ID</div>
                      <div className="info-value">{deal.deal_iq_id || deal.id || '--'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">VOUCHER UNIQUE ID</div>
                      <div className="info-value">{deal.voucher_unique_id || '--'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">PURSUIT ID</div>
                      <div className="info-value">{deal.pursuit_id || 'Pending'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">CONNECTOR ID</div>
                      <div className="info-value">{deal.connector_id || '--'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">DEAL TYPE</div>
                      <div className="info-value">{formatDealTypeAndRole(deal.deal_type, deal.rep_role)}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">DEAL STAGE</div>
                      <div className="info-value">{formatDealStage(deal.deal_life_cycle_stage)}</div>
                    </div>
                    {/* REP ROLE removed as requested */}
                    {/* IS MTA removed as requested */}
                    <div className="info-item">
                      <div className="info-label">CREATED BY</div>
                      <div className="info-value">
                        {typeof deal.created_by === 'string' 
                          ? deal.created_by 
                          : getUserName(deal.created_by) || '--'}
                        {deal.created_by?.work_email && (
                          <span className="lead-producer-email-card">
                            <svg className="lead-producer-envelope" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2.5 5.5A2.5 2.5 0 0 1 5 3h10a2.5 2.5 0 0 1 2.5 2.5v9A2.5 2.5 0 0 1 15 17H5a2.5 2.5 0 0 1-2.5-2.5v-9Zm2.1.5 5.4 4.5 5.4-4.5" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span className="lead-producer-email-text">{deal.created_by.work_email}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">LEAD PRODUCER</div>
                        <div className="info-value">
                          {getLeadProducerName(deal.lead_producer)}
                          {deal.lead_producer?.broker?.work_email && (
                            <span className="lead-producer-email-card">
                              <svg className="lead-producer-envelope" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 5.5A2.5 2.5 0 0 1 5 3h10a2.5 2.5 0 0 1 2.5 2.5v9A2.5 2.5 0 0 1 15 17H5a2.5 2.5 0 0 1-2.5-2.5v-9Zm2.1.5 5.4 4.5 5.4-4.5" stroke="#009688" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span className="lead-producer-email-text">{deal.lead_producer.broker.work_email}</span>
                            </span>
                          )}
                        </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">CREATED DATE</div>
                      <div className="info-value">{formatDate(deal.created_date)}</div>
                    </div>
                  </div>

                  {/* Linked MTA Transactions Section - Full Width */}
                  <div className="linked-mta-transactions-section">
                    <div className="linked-mta-header">
                      <span className="linked-mta-label">LINKED MTA TRANSACTIONS</span>
                    </div>
                    {deal.linked_mta_transactions && deal.linked_mta_transactions.length > 0 ? (
                      <>
                        <div className="linked-mta-transactions-grid">
                          {deal.linked_mta_transactions.map((tx, idx) => (
                            <button
                              key={tx.transaction_id}
                              className={`linked-mta-transaction-btn ${loadingTransactionId === tx.transaction_id ? 'loading' : ''} ${selectedTransactionId === tx.transaction_id ? 'selected' : ''}`}
                              onClick={() => handleLinkedMtaTransactionClick(tx.transaction_id)}
                              disabled={loadingTransactionId === tx.transaction_id}
                            >
                              {loadingTransactionId === tx.transaction_id ? (
                                <>
                                  <Icon iconName="spinner" className="fa-spin" />
                                  <span className="admin-loading-text">Loading...</span>
                                </>
                              ) : (
                                <>
                                  {tx.transaction_id} &nbsp;-&nbsp; {tx.deal_id}
                                </>
                              )}
                            </button>
                          ))}
                        </div>
                        
                        {/* Render expanded transaction details here instead of at parent level */}
                        {selectedTransactionId && expandedDealId === deal.deal_iq_id && (
                          <div className="linked-mta-expanded-content">
                            {commissionOverviewSection(deal)}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="linked-mta-no-transactions">
                        <span className="no-transactions-text">--</span>
                      </div>
                    )}
                  </div>

                  {/* Connector ID Section */}
                  <div className="collaborators-section">
                    <div className="info-item">
                      <div className="info-label">VOUCHER SUBMISSION ACCESS</div>
                      <div className="info-value connector-id-value" style={{ marginBottom: '12px' }}>
                        {/* Conditional button based on connector_id, salesforce_id, and restrictions */}
                        <VoucherAccessConditionalButton 
                          deal={deal}
                          showAccess={showConnectorAccess}
                          onToggle={handleConnectorAccessToggle}
                          dealUsers={dealUsers}
                          dealTeams={dealTeams}
                          dealRestrictions={dealRestrictions}
                          restrictionsLoading={restrictionsLoading}
                        />
                      </div>
                    </div>
                    
                    {/* Show connector voucher access only when expanded */}
                    {showConnectorAccess && (
                      <div className="connector-access-container">
                        <AdminConnectorVoucherAccessNew 
                          dealId={deal.id}
                          connectorId={connectorId}
                          dealType={deal.deal_type}
                          adminUserId={userData?.id}
                          dealUsers={dealUsers}
                          dealTeams={dealTeams}
                          dealRestrictions={dealRestrictions}
                        />
                      </div>
                    )}
                  </div>

                  {/* Render commission overview section only when no transaction is selected */}
                  {!selectedTransactionId && commissionOverviewSection}

                  {/* MTA Collaborators Section */}
                  {deal.collaborators_mta?.length > 0 && (
                    <div className="collaborators-section">
                      <div className="collaborators-header">
                        <span className="collaborators-label">MTA Collaborators</span>
                        <div className="collaborators-actions">
                          <span className="info-text">From linked MTA deal</span>
                        </div>
                      </div>
                      
                      <div className="collaborators-subsection">
                        <div className="collaborators-list">
                          {deal.collaborators_mta.map((collaborator, index) => (
                            <div key={`mta-${collaborator.user_id}-${index}`} className="collaborator-item mta-collaborator-item">
                              <div className="collaborator-avatar">
                                {collaborator.first_name || collaborator.last_name ? (
                                  <div 
                                    className="avatar-placeholder"
                                    style={{ backgroundColor: getAvatarColor(collaborator.first_name, collaborator.last_name, collaborator.user_id) }}
                                  >
                                    {(collaborator.first_name?.charAt(0) || '') + (collaborator.last_name?.charAt(0) || '')}
                                  </div>
                                ) : (
                                  <div className="avatar-placeholder">
                                    MTA
                                  </div>
                                )}
                                <div className="mta-indicator">MTA</div>
                              </div>
                              <div className="collaborator-details">
                                <div className="collaborator-name">
                                  {collaborator.first_name} {collaborator.last_name}
                                  {collaborator.role && <span className="role-badge">{collaborator.role}</span>}
                                </div>
                                <div className="collaborator-info">
                                  {collaborator.email || 'No email available'}
                                </div>
                                {collaborator.team_name && (
                                  <div className="team-info">
                                    <Icon iconName="users" />
                                    <span>{collaborator.team_name}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Voucher Submission Section - Button Pattern */}
                  <div className="collaborators-section">
                    <div className="collaborators-header">
                      <span className="collaborators-label">
                        <Icon iconName="file-invoice-dollar" />
                        Voucher Submissions History
                      </span>
                    </div>
                    
                    {/* Button positioned like linked MTA transactions */}
                    {prepareVoucherData(deal.voucher_submission).hasVouchers && (
                      <div style={{ marginBottom: '12px' }}>
                        <button
                          className="linked-mta-transaction-btn"
                          onClick={() => handleVoucherSubmissionsClick(deal.id)}
                          title={showVoucherSubmissions === deal.id ? 'Hide voucher submissions' : 'Show voucher submissions'}
                        >
                          <Icon iconName={showVoucherSubmissions === deal.id ? 'chevron-up' : 'file-invoice'} />
                          {showVoucherSubmissions === deal.id ? 'Hide' : 'View'} History
                        </button>
                      </div>
                    )}
                    
                    {/* Show voucher submissions only when expanded */}
                    {showVoucherSubmissions === deal.id && prepareVoucherData(deal.voucher_submission).hasVouchers && (
                      <div className="collaborators-subsection">
                        <div className="voucher-list">
                          <AdminVoucherSubmissionTable history={prepareVoucherData(deal.voucher_submission).vouchers} />
                        </div>
                      </div>
                    )}
                    
                    {/* Show message when no vouchers available */}
                    {!prepareVoucherData(deal.voucher_submission).hasVouchers && (
                      <div className="collaborators-subsection">
                        <div className="no-collaborators no-vouchers">No voucher submissions found for this deal</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
    </RowErrorBoundary>
  );
};

AdminDealDetails.propTypes = {
  deal: PropTypes.shape({
    connector_id: PropTypes.string,
    teams: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  expandedDealId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loadingTeamData: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dealUsers: PropTypes.arrayOf(PropTypes.shape()),
  dealTeams: PropTypes.arrayOf(PropTypes.shape()),
  reduxDeal: PropTypes.shape(),
  addAllAdminDealMembers: PropTypes.func.isRequired,
  getCommissionUserIds: PropTypes.func.isRequired,
  loadingTransactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedTransactionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleLinkedMtaTransactionClick: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  showVoucherSubmissions: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleVoucherSubmissionsClick: PropTypes.func.isRequired,
  userData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  toast: PropTypes.func,
  commissionOverviewSection: PropTypes.node, // JSX node for commission overview
};

export default AdminDealDetails;
